import React, { useState } from 'react';
import { Icon, Avatar, Field } from '@/components/ui';
import { useIsMobile } from '@/components/main/shell/useIsMobile';
import { openFeedback } from '@/components/main/shell/feedback';
import { useClientCompanies } from './hooks/useClientCompanies';
import { filterCompanies } from './lib/filterCompanies';

interface ClientCompaniesProps {
  clientName: string;
  /** Called with the selected account id. */
  onOpen: (id: number) => void;
}

export const ClientCompanies = ({
  clientName,
  onOpen,
}: ClientCompaniesProps) => {
  const mobile = useIsMobile();
  const { companies, isLoading, isError } = useClientCompanies();
  const [q, setQ] = useState('');
  const multi = companies.length > 1;
  const rows = filterCompanies(companies, q);
  const firstName = (clientName || '').split(' ')[0] || 'there';

  return (
    <div
      style={{
        minHeight: mobile ? 'calc(100vh - 120px)' : '100vh',
        background: 'var(--table-header-bg)',
        padding: mobile ? '20px 16px 32px' : '40px',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: mobile ? 22 : 28,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: 'var(--ink)',
                margin: '0 0 4px',
              }}
            >
              Welcome back, {firstName}
            </h1>
            <p className="app-sm" style={{ margin: '0 0 24px' }}>
              {isLoading
                ? 'Loading your companies…'
                : `You have access to ${companies.length} ${companies.length === 1 ? 'company' : 'companies'}. Select one to open its workspace.`}
            </p>
          </div>
          <button
            type="button"
            onClick={openFeedback}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = 'rgba(0,68,157,.05)')
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              height: 38,
              padding: mobile ? '0 14px' : '0 16px',
              borderRadius: 9999,
              flex: 'none',
              border: '1.5px solid var(--twilight-sky)',
              background: '#fff',
              color: 'var(--twilight-sky)',
              fontFamily: 'var(--font-sans)',
              fontSize: 13.5,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background .12s',
            }}
          >
            <Icon name="megaphone" size={16} color="var(--twilight-sky)" />
            {mobile ? 'Feedback' : 'Give feedback'}
          </button>
        </div>

        {multi && (
          <div style={{ marginBottom: 16 }}>
            <Field
              icon="search"
              placeholder="Search companies…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onClear={() => setQ('')}
            />
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {isError && (
            <div
              role="alert"
              style={{
                padding: '48px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                color: 'var(--ink-mute)',
                background: '#fff',
                border: '1px solid var(--hairline)',
                borderRadius: 'var(--radius-l)',
              }}
            >
              <Icon name="triangle-alert" size={28} color="var(--danger)" />
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 14,
                  textAlign: 'center',
                }}
              >
                We couldn’t load your companies. Please refresh and try again.
              </span>
            </div>
          )}
          {rows.map((c) => (
            <button
              key={c.id}
              type="button"
              aria-label={`Open ${c.name}`}
              onClick={() => c.active && onOpen(c.id)}
              disabled={!c.active}
              style={{
                textAlign: 'left',
                border: '1px solid var(--hairline)',
                borderRadius: 'var(--radius-l)',
                background: '#fff',
                padding: 0,
                cursor: c.active ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 'var(--e1)',
                fontFamily: 'var(--font-sans)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: mobile ? '16px 16px 14px' : '20px 22px 16px',
                }}
              >
                <span
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 'var(--radius-m)',
                    background: 'var(--sky-mist)',
                    border: '1px solid var(--sky-mist-border)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 'none',
                  }}
                >
                  <Icon
                    name="building-2"
                    size={23}
                    color="var(--twilight-sky)"
                  />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: mobile ? 16 : 17,
                      fontWeight: 600,
                      color: 'var(--ink)',
                      letterSpacing: '-0.01em',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {c.name}
                  </div>
                  <div style={{ marginTop: 5 }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 12,
                        fontWeight: 500,
                        color: c.active
                          ? 'var(--success-text)'
                          : 'var(--ink-mute)',
                      }}
                    >
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: 9999,
                          background: c.active
                            ? 'var(--icon-success)'
                            : 'var(--ink-mute)',
                        }}
                      />
                      {c.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                {/* No open affordance on inactive companies — the card can't be opened. */}
                {c.active && (
                  <span
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 9999,
                      background: 'var(--sky-mist)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 'none',
                    }}
                  >
                    <Icon
                      name="arrow-right"
                      size={17}
                      color="var(--twilight-sky)"
                    />
                  </span>
                )}
              </div>
              {c.managerName && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: mobile ? '12px 16px' : '14px 22px',
                    borderTop: '1px solid var(--hairline)',
                    background: 'var(--table-header-bg)',
                  }}
                >
                  <Avatar
                    name={c.managerName}
                    src={c.managerAvatarUrl || undefined}
                    size={28}
                  />
                  <div style={{ lineHeight: 1.35, minWidth: 0 }}>
                    <div
                      className="app-label"
                      style={{ color: 'var(--ink-mute)' }}
                    >
                      CloudCFO manager
                    </div>
                    <div
                      style={{
                        fontSize: 13.5,
                        fontWeight: 500,
                        color: 'var(--ink-soft)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {c.managerName}
                    </div>
                  </div>
                </div>
              )}
            </button>
          ))}

          {!isLoading && !isError && rows.length === 0 && (
            <div
              style={{
                padding: '48px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                color: 'var(--ink-mute)',
                background: '#fff',
                border: '1px solid var(--hairline)',
                borderRadius: 'var(--radius-l)',
              }}
            >
              <Icon name="search-x" size={28} color="var(--ink-mute)" />
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 14,
                  textAlign: 'center',
                }}
              >
                {q.trim()
                  ? `No companies match “${q}”.`
                  : 'No companies are assigned to you yet.'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientCompanies;
